import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ReactComponent as ClearIcon } from 'images/clear.svg'

import './input.style.css'

const DEFAULT_DEBOUNCE_TIME = 300

type Props = {
  onChange: (value: string) => void
  onFocus?: () => void
  value?: string
  placeHolder?: string
  displayClearIcon?: boolean
  labelText?: string
  isError?: boolean
  readOnly?: boolean
  debounce?: {
    duration: number
  }
}

export const Input = ({ onChange, debounce, ...props }: Props) => {
  const ref = useRef<HTMLInputElement>(null)
  const timeout = useRef<NodeJS.Timeout>(null)
  const [stateValue, setStateValue] = useState<string>(props.value)

  const handleOnChange = useCallback(() => {
    if (!ref.current) {
      return
    }

    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    timeout.current = setTimeout(() => {
      onChange(ref.current.value)
    }, debounce?.duration || DEFAULT_DEBOUNCE_TIME)

    setStateValue(ref.current.value)
  }, [debounce, onChange])

  useEffect(() => {
    if (ref.current && typeof props.value !== 'undefined') {
      ref.current.value = props.value
      setStateValue(ref.current.value)
    }
  }, [props.value])

  useEffect(() => () => clearTimeout(timeout.current), [])

  const renderIcon = useCallback(() => {
    const handleClear = () => {
      if (!ref.current) {
        return
      }

      ref.current.value = ''
      onChange(ref.current.value)
      setStateValue(ref.current.value)
    }

    const classname = classNames('input-icon', {
      'input-icon__with-text': props.labelText,
    })

    if (!props.displayClearIcon && !props.readOnly) {
      return <ClearIcon className={classname} onClick={handleClear} />
    }

    return null
  }, [props.labelText, onChange, props.displayClearIcon, props.readOnly])

  const inputClassName = classNames('text-field', 'custom-input', {
    'text-field--error': props.isError,
  })

  return (
    <div className="input__wrapper">
      <div className="input__row">
        {props.labelText && <label>{props.labelText}</label>}
        <div className="input-container">
          <input
            type="text"
            ref={ref}
            readOnly={props.readOnly}
            onChange={handleOnChange}
            onFocus={props.onFocus}
            placeholder={props.placeHolder}
            value={stateValue}
            className={inputClassName}
          />
          {renderIcon()}
        </div>
      </div>
    </div>
  )
}
